import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { Dropdown } from "../../../shadcn/components/custom/Dropdown";
import { Input } from "../../../shadcn/components/ui/input";
import { Switch } from "../../../shadcn/components/ui/switch";
import VerifiedHammer from "../../../icons/VerifiedHammer";
import ChevronUpSvg from "../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../icons/ChevronDownSvg";
import MagnifyingGlassIcon from "../../../icons/MagnifyingGlassIcon";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../shadcn/components/ui/popover";
import { Calendar } from "../../../shadcn/components/ui/calendar";
import { Checkbox } from "../../../shadcn/components/ui/checkbox";
import { Context, getSearchResults, formatIndianCurrency } from "services";
import Dot from "../../../icons/Dot";
import PriceRangeSelector from "../propertyList/PriceRangeSelector";
import { X } from "lucide-react";
import BrandedButton from "../../../shadcn/components/custom/BrandedButton";
import { OptionType } from "../postYourProperty/listingPageV2";

interface AuctionPropertyListPageSidebarFilterMenuPropsInterface {
  filtersOpen: boolean;
  onClearFilters: () => void;
  onClose: () => void;
  onFiltersApply: () => void;
  setIsFilterOpen: (value: boolean) => void;
}

const scheduleData = [
  { label: "Scheduled ", value: "dated" },
  { label: "Upcoming ", value: "undated" },
  { label: "Private Treaty", value: "private_treaty" },
];

const bankName = process.env.NEXT_PUBLIC_BANK;

export const AuctionPropertyListPageSidebarFilterMenu: React.FC<
  AuctionPropertyListPageSidebarFilterMenuPropsInterface
> = ({
  onClearFilters = () => {},
  filtersOpen,
  onClose = () => {},
  onFiltersApply = () => {},
  setIsFilterOpen = () => {},
}: AuctionPropertyListPageSidebarFilterMenuPropsInterface) => {
  const { searchForm }: any = useContext(Context);

  const [assetTypeOptions, setAssetTypeOptions] = useState<any[]>([]);

  const [clearInternalDateSelection, setClearInternalDateSelection] =
    useState(false);

  const filterData = (dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => ({
      label: data?.parent?.asset_type_name,
      value: data?.parent?.asset_type_slug,
    }));
  };

  const getFilterForSubAssetType = (searchForm: any) => {
    const filters = [];
    if (searchForm?.value?.asset_type?.value) {
      const currentFilterString = `dump.asset.asset_type_slug:=${searchForm?.value?.asset_type?.value}`;
      filters.push(currentFilterString);
    }
    return filters;
  };

  const fetchOptions = async (): Promise<any[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          query_by: "",
          collection: "properties",
          q: "*",
          facet_by: "dump.asset.asset_type_name,dump.asset.asset_type_slug,",
          facet_return_parent:
            "dump.asset.asset_type_name,dump.asset.asset_type_slug,",
          filter: "true",
          max_facet_values: "500",
          infix: "always",
        },
        {
          query_by: "",
          collection: "properties",
          q: "*",
          facet_by:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          facet_return_parent:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: getFilterForSubAssetType(searchForm).join(" && "),
          max_facet_values: "500",
          infix: "always",
        },
      ],
    });

    return (
      initialData?.results?.map((result: any) => ({
        group: "popular",
        data: filterData(result?.facet_counts?.[0]?.counts),
      })) || []
    );
  };

  useEffect(() => {
    const initializeOptions = async () => {
      const data = await fetchOptions();
      setAssetTypeOptions(data[0]?.data || []);
    };
    initializeOptions();
  }, []);

  const handleSelectedScheduleOption = (value: any) => {
    searchForm.setField("filter_by", value);
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dataSource: () => Promise<any> = () => {
    return Promise.resolve(assetTypeOptions);
  };

  const handleSetSelectedOption = (item: any) => {
    const currentAssetType = searchForm.getField("asset_type");
    searchForm.setField("asset_type", item);
    if (
      currentAssetType &&
      currentAssetType.value &&
      currentAssetType.value !== item.value
    ) {
      searchForm.setField("sub_asset_type", "");
    }
  };

  const auctionVerifiedData = [
    { label: "Verified Property", value: "auction_verified" },
  ];

  const propertyListingData = [
    { label: "Auction Properties ", value: "dated" },
    { label: "Private Properties ", value: "undated" },
  ];

  const handleListingTypeOption = (data: OptionType) => {
    if (!bankName) {
      if (data?.value === "dated") {
        window.location.href = "/auction-properties";
      } else {
        window.location.href =
          searchForm.value.city.value === ""
            ? "/private-properties"
            : `/private-properties?city=${searchForm?.value?.city?.value}`;
      }
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    const filterField = searchForm.getField("filter_by");
    if (checked) {
      searchForm.setField("ab_verified", {
        label: "Verified Property",
        value: "auction_verified",
      });
    } else {
      const isFilterVerified =
        filterField &&
        auctionVerifiedData?.some((item) => filterField.value === item.value);

      if (isFilterVerified) {
        searchForm.setField("filter_by", "");
      }
      searchForm.setField("ab_verified", "");
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    searchForm.setField("listing_id_query", value);
  };

  const handleClearFilter = () => {
    setClearInternalDateSelection(true);
    onClearFilters();
  };

  return (
    <section
      className={`fixed top-0 left-0 z-40 h-full w-[340px] bg-white border-r border-gray-200 p-4 overflow-y-auto transition-transform duration-300 ${
        filtersOpen ? "translate-x-0" : "-translate-x-full"
      }
  `}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-md md:text-xl lg:text-2xl font-semibold">
          Filters
        </h3>
        <button
          onClick={onClose}
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>

      <div className="mx-auto flex flex-col items-center gap-4 py-4 2xl:px-0">
        {/* LEFT FILTERS */}
        <div
          ref={scrollContainerRef}
          className="flex flex-col flex-1 items-center gap-3 w-full overflow-y-auto"
        >
          {/* SEARCH ID */}
          <Input
            type="text"
            placeholder="Search ID"
            value={searchForm.getField("listing_id_query") || ""}
            onChange={handleInputChange}
            className="h-9 w-full rounded-md border-gray-300 text-sm"
          />

          {/* ASSET TYPE */}
          <Dropdown
            dataSource={dataSource}
            defaultSelected={searchForm.getField("asset_type") || undefined}
            selectIdentifier
            onChange={handleSetSelectedOption}
            valueKey="value"
            placeholder="Asset Type"
            styles={{
              outerContainer:
                "h-9 rounded-md w-full border border-gray-300 px-4 text-sm",
            }}
          />

          {/* SUB ASSET */}
          <PropertySubTypeFilter />

          {/* PRICE */}
          <PriceRangeBasicDropDown />

          {/* DATE */}
          <DateSelector
            clear={clearInternalDateSelection}
            setClear={setClearInternalDateSelection}
          />

          {/* BANK */}

          {!bankName && <InstitutesFilter />}

          {/* SCHEDULE */}
          <Dropdown
            dataSource={() => Promise.resolve(scheduleData)}
            defaultSelected={
              searchForm.getField("filter_by") !== ""
                ? scheduleData.find(
                    (item) =>
                      searchForm.getField("filter_by").value === item.value,
                  )
                : undefined
            }
            selectIdentifier
            onChange={handleSelectedScheduleOption}
            valueKey="value"
            placeholder="Schedule"
            styles={{
              outerContainer:
                "h-9 rounded-md border border-gray-300 px-4 text-sm",
            }}
          />

          {!bankName && (
            <Dropdown
              dataSource={() => {
                return Promise.resolve(propertyListingData);
              }}
              defaultSelected={undefined}
              selectIdentifier={true}
              onChange={(data: any) => handleListingTypeOption(data)}
              valueKey="value"
              placeholder="Auction Properties"
              styles={{
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer:
                  "border-[1px] border-brand-borderGrey hover:border-brand-orange w-full rounded-lg text-sm leading-normal font-openSans text-brand-textBlack px-4",
              }}
            />
          )}

          {/* VERIFIED */}
          <div className="flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 w-full justify-center">
            <VerifiedHammer />
            <Switch
              checked={
                searchForm.getField("filter_by") !== ""
                  ? auctionVerifiedData.find(
                      (item) =>
                        searchForm.getField("filter_by").value === item.value,
                    )
                    ? true
                    : searchForm.getField("ab_verified") &&
                      searchForm.getField("ab_verified") !== ""
                    ? true
                    : false
                  : searchForm.getField("ab_verified") &&
                    searchForm.getField("ab_verified") !== ""
                  ? true
                  : false
              }
              onCheckedChange={handleSwitchChange}
            />
          </div>
        </div>
        <BrandedButton
          variant="primary"
          className="md:block hidden"
          fullWidth
          onClick={onFiltersApply}
        >
          Apply Filters
        </BrandedButton>
        <BrandedButton
          variant="primary"
          className="md:hidden block"
          fullWidth
          onClick={() => {
            onFiltersApply();
            setIsFilterOpen(false);
          }}
        >
          Apply Filters
        </BrandedButton>

        <BrandedButton
          variant="outline"
          className="md:block hidden"
          fullWidth
          onClick={handleClearFilter}
        >
          Clear Filters
        </BrandedButton>
        <BrandedButton
          variant="outline"
          fullWidth
          className="md:hidden block"
          onClick={() => {
            handleClearFilter();
            setIsFilterOpen(false);
          }}
        >
          Clear Filters
        </BrandedButton>
      </div>
    </section>
  );
};

const PriceRangeBasicDropDown = () => {
  const { searchForm }: any = useContext(Context);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<
    [number | null, number | null]
  >([0, null]);

  // Update the reserve price field when the selected range changes
  useEffect(() => {
    const reservePrice = searchForm.value.reserve_price;
    if (reservePrice) {
      const [minPrice, maxPrice] = reservePrice.split("-").map(Number);
      setSelectedRange([minPrice * 100000, maxPrice * 100000]);
    } else {
      setSelectedRange([0, null]);
    }
  }, [searchForm.value.reserve_price]);

  useEffect(() => {
    const updateReservePrice = () => {
      if ((selectedRange[0] || selectedRange[0] === 0) && selectedRange[1]) {
        const formattedRange = `${Math.floor(
          selectedRange[0] / 100000,
        )}-${Math.floor(selectedRange[1] / 100000)}`;
        if (isOpen && selectedRange[0] < selectedRange[1]) {
          searchForm.setField("reserve_price", formattedRange);
        }
      }
    };

    const handler = setTimeout(updateReservePrice, 300);
    return () => clearTimeout(handler);
  }, [selectedRange, isOpen]);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[130px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <div className="flex items-center justify-between">
            {searchForm.getField("reserve_price") &&
            searchForm.getField("reserve_price") !== "" ? (
              <p className="text-[13px] font-openSans text-brand-textBlack flex items-center whitespace-nowrap">
                <span className="mr-3">
                  <Dot />
                </span>
                ₹{formatIndianCurrency(selectedRange[0])} - ₹
                {formatIndianCurrency(selectedRange[1])}
              </p>
            ) : (
              <p className="text-[14px] font-openSans text-brand-textBlack flex items-center whitespace-nowrap">
                Price Range
              </p>
            )}
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill="#7D7D7D" />
              ) : (
                <ChevronDownSvg width={14} height={14} fill="#7D7D7D" />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="min-w-[439px]">
        <PriceRangeSelector
          range={[0, 2600000000]}
          defaultSelectedRange={selectedRange}
          minStepsBetweenThumbs={500}
          step={500}
          setSelectedRange={setSelectedRange}
        />
      </PopoverContent>
    </Popover>
  );
};

const DateSelector = ({
  clear,
  setClear,
}: {
  clear: boolean;
  setClear: (clear: boolean) => void;
}) => {
  const { searchForm }: any = useContext(Context);
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | any>(null);
  const [endDate, setEndDate] = useState<Date | any>(null);
  const [dateRangeValue, setDateRangeValue] = useState<string | null>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const dateToUnixTimestamp = (date: Date | undefined): number | null => {
    return date ? Math.floor(date.getTime()) : null;
  };

  const validateAndPrepareSearch = useCallback(() => {
    if (startDate && endDate && startDate > endDate) {
      return null;
    } else {
    }
    const startTimestamp = dateToUnixTimestamp(startDate);
    const endTimestamp = dateToUnixTimestamp(endDate);
    if (startTimestamp && endTimestamp) {
      setDateRangeValue(`${startTimestamp}-${endTimestamp}`);
    } else return setDateRangeValue(null);
  }, [startDate, endDate]);

  useEffect(() => {
    validateAndPrepareSearch();
    if (dateRangeValue) {
      searchForm.setField("auction_start_date_unix_timestamp", dateRangeValue);
    }
  }, [dateRangeValue, startDate, endDate]);

  const clearDateTimestamp = () => {
    setStartDate(null);
    setEndDate(null);
    setDateRangeValue(null);
    setClear(false);
  };

  useEffect(() => {
    const auctionStartEndDateTimeStamp =
      searchForm.value.auction_start_date_unix_timestamp;

    if (!auctionStartEndDateTimeStamp || auctionStartEndDateTimeStamp === "") {
      clearDateTimestamp();
    }
  }, [searchForm.value.auction_start_date_unix_timestamp]);

  useEffect(() => {
    if (clear) {
      clearDateTimestamp();
    }
  }, [clear]);

  const handleDateSelect = (range: { from?: Date; to?: Date }) => {
    if (!range) return;

    const { from: selectedStartDate, to: selectedEndDate } = range;

    if (startDate && endDate) {
      const midpointTimestamp = (startDate.getTime() + endDate.getTime()) / 2;
      if (
        selectedStartDate &&
        selectedStartDate.getTime() <= midpointTimestamp
      ) {
        setStartDate(selectedStartDate);
      }
      if (selectedEndDate && selectedEndDate.getTime() >= midpointTimestamp) {
        setEndDate(selectedEndDate);
      } else if (
        selectedEndDate &&
        selectedEndDate.getTime() < midpointTimestamp
      ) {
        setStartDate(selectedEndDate);
      }
    } else if (startDate && !endDate) {
      if (
        selectedStartDate &&
        selectedStartDate.getTime() < startDate.getTime()
      ) {
        setEndDate(startDate);
        setStartDate(selectedStartDate);
      } else if (
        selectedEndDate &&
        selectedEndDate.getTime() >= startDate.getTime()
      ) {
        setEndDate(selectedEndDate);
      }
    } else {
      setStartDate(selectedStartDate || null);
      setEndDate(selectedEndDate || null);
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[190px] xl:min-w-[214px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={toggleDropdown}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-openSans text-brand-textBlack flex items-center">
              {startDate && endDate && (
                <span className="mr-3">
                  <Dot />
                </span>
              )}
              {`${formatDate(startDate) || "Start date"} - ${
                formatDate(endDate) || "End date"
              }`}
            </p>
            <div className="pr-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill="#7D7D7D" />
              ) : (
                <ChevronDownSvg width={14} height={14} fill="#7D7D7D" />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="flex w-auto flex-col space-y-2 px-[51px] pt-7">
        <div className="w-full bg-[#EEE] grid grid-cols-2 justify-between rounded-lg py-4 mb-4">
          <div className="flex flex-col justify-center items-center border-r-[1px] border-brand-textLightGrey">
            <div className="text-[10px] font-openSans text-brand-bgBlack">
              Start date
            </div>
            <div className="text-xs font-openSans text-brand-bgBlack">
              {startDate ? formatDate(startDate) : "Add dates"}
            </div>
          </div>
          <div className="flex flex-col justify-center items-center">
            <div className="text-[10px] font-openSans text-brand-bgBlack">
              End date
            </div>
            <div className="text-xs font-openSans text-brand-bgBlack">
              {endDate ? formatDate(endDate) : "Add dates"}
            </div>
          </div>
        </div>
        <div className="rounded-md border">
          <Calendar
            initialFocus
            mode="range"
            selected={{
              from: startDate || undefined,
              to: endDate || undefined,
            }}
            onSelect={(range: { from?: Date; to?: Date } | undefined) => {
              if (range) {
                handleDateSelect(range);
              }
            }}
            numberOfMonths={2}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const InstitutesFilter = () => {
  const { searchForm }: any = useContext(Context);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [options, setOptions] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const filterData = useCallback(
    (dataToFilter: any) =>
      (dataToFilter || []).map((data: any) => ({
        label: data.parent.bank_name,
        value: data.parent.bank_slug,
      })),
    [],
  );

  const fetchOptions = useCallback(async (): Promise<any[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          filter_by: `${
            searchForm.value.city.value
              ? `status:active && dump.location.city_name:${searchForm.value.city.label}`
              : "status:active"
          }`,
          filter: "true",
          facet_by: `dump.bank.bank_name(sort_by: _alpha:${sortOrder})`,
          facet_return_parent: "dump.bank.bank_name",
          max_facet_values: "500",
          per_page: 0,
        },
      ],
    });

    const filteredData = filterData(
      initialData?.results?.[0]?.facet_counts?.[0]?.counts || [],
    );

    return [
      {
        group: "popular",
        data: filteredData,
      },
    ];
  }, [
    searchForm.value.city.label,
    searchForm.value.city.value,
    sortOrder,
    filterData,
  ]);

  useEffect(() => {
    const initializeOptions = async () => {
      const data = await fetchOptions();
      setOptions(data[0]?.data || []);
    };
    initializeOptions();
  }, [fetchOptions]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleCheckboxChange = (
    bank: { label: string; value: string },
    isChecked: boolean,
  ) => {
    const prev = searchForm.getField("institutes");

    if (Array.isArray(prev) && isChecked) {
      const selectedPrev = prev.filter(
        (item: any) => item?.value !== bank?.value,
      );
      selectedPrev.push(bank);
      searchForm.setField("institutes", selectedPrev);
    } else if (Array.isArray(prev) && !isChecked) {
      const selectedPrev = prev.filter(
        (item: any) => item?.value !== bank?.value,
      );
      if (selectedPrev.length > 0) {
        searchForm.setField("institutes", selectedPrev);
      } else {
        searchForm.setField("institutes", "");
      }
    } else if (isChecked) {
      searchForm.setField("institutes", [bank]);
    }
  };

  const filteredOptions = useMemo(
    () =>
      options.filter((bank: any) =>
        bank.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
      ),
    [options, debouncedSearchTerm],
  );

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[130px] xl:min-w-[154px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={toggleDropdown}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-openSans text-brand-textBlack flex items-center">
              {Array.isArray(searchForm.getField("institutes")) &&
                searchForm.getField("institutes").length > 0 && (
                  <span className="mr-3">
                    <Dot />
                  </span>
                )}
              Institutes
            </p>
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="min-w-[203px]">
        <div className="min-w-[203px]">
          <div className="flex py-2 items-center">
            <MagnifyingGlassIcon />
            <Input
              type="text"
              placeholder="Search"
              className="h-5 border-none rounded-sm text-xs ring-none outline-none focus:border-0 focus:outline-none px-0 ml-3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between py-2 border-t-[1px]">
            <span className="text-xs font-openSans text-brand-textLightGrey">
              Sort by name
            </span>
            <button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="flex items-center text-xs font-openSans text-brand-textLightGrey hover:text-brand-textBlack"
            >
              {sortOrder === "asc" ? "A-Z" : "Z-A"}
              <div className="ml-1">
                {sortOrder === "asc" ? (
                  <ChevronUpSvg width={12} height={12} fill="#7D7D7D" />
                ) : (
                  <ChevronDownSvg width={12} height={12} fill="#7D7D7D" />
                )}
              </div>
            </button>
          </div>
          <div className="max-h-[251px] overflow-scroll border-t-[1px] py-4">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((bank, index) => (
                <div key={index} className="flex items-center">
                  <Checkbox
                    checkIconClassName="text-brand-bgBlack"
                    className="data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack"
                    id={`bank-${index}`}
                    checked={
                      Array.isArray(searchForm.getField("institutes")) &&
                      searchForm
                        .getField("institutes")
                        ?.some((item: any) => item.value === bank.value) // Check if the current bank is selected
                    }
                    onCheckedChange={
                      (isChecked: boolean) =>
                        handleCheckboxChange(bank, isChecked) // Pass the entire bank object
                    }
                  />
                  <label
                    htmlFor={`bank-${index}`}
                    className="text-sm pl-2 py-1 font-openSans capitalize tracking-wider text-brand-textLightGrey"
                  >
                    {bank.label}
                  </label>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 pl-2 py-3">
                No results found
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PropertySubTypeFilter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { searchForm }: any = useContext(Context);
  const initialCheckedItems = searchForm.value.asset_type
    ? Array.isArray(searchForm.value.sub_asset_type)
      ? searchForm.value.sub_asset_type
      : typeof searchForm.value.sub_asset_type === "object" &&
        searchForm.value.sub_asset_type !== ""
      ? [searchForm.value.sub_asset_type]
      : []
    : [];

  const [assetSubTypeOptions, setAssetSubTypeOptions] = useState<any[]>([]);

  const filterData = useCallback((dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => ({
      label: data.parent.sub_asset_type_name,
      value: data.parent.sub_asset_type_slug,
    }));
  }, []);

  const getFilterForSubAssetType = (searchForm: any) => {
    const filters = [];
    if (searchForm.value.asset_type.value) {
      const currentFilterString = `dump.asset.asset_type_slug:=${searchForm.value.asset_type.value}`;
      filters.push(currentFilterString);
    }
    return filters;
  };

  const fetchOptions = async (): Promise<any[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          query_by: "",
          collection: "properties",
          q: "*",
          facet_by:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          facet_return_parent:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: getFilterForSubAssetType(searchForm).join(" && "),
          max_facet_values: "500",
          infix: "always",
        },
      ],
    });

    return (
      initialData?.results?.map((result: any) => ({
        group: "popular",
        data: filterData(result.facet_counts?.[0]?.counts),
      })) || []
    );
  };

  useEffect(() => {
    if (searchForm.value.asset_type.value) {
      const initializeOptions = async () => {
        const data = await fetchOptions();
        setAssetSubTypeOptions(data[0]?.data || []);
      };
      initializeOptions();
    } else {
      setAssetSubTypeOptions([]);
    }
  }, [searchForm.value.asset_type]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleCheckboxChange = (
    item: { label: string; value: string },
    isChecked: boolean,
  ) => {
    const updatedCheckedItems = isChecked
      ? [...initialCheckedItems, item]
      : initialCheckedItems.filter(
          (checkedItem: { value: string }) => checkedItem.value !== item.value,
        );
    searchForm.setField("sub_asset_type", updatedCheckedItems);
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[130px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={toggleDropdown}
        >
          <div className="flex items-center justify-between">
            <div className="text-sm font-openSans text-brand-textBlack capitalize overflow-hidden break-all max-h-5 flex items-center">
              {initialCheckedItems.length > 0 && (
                <span className="mr-3">
                  <Dot />
                </span>
              )}

              <span className="overflow-hidden text-ellipsis whitespace-nowrap capitalize">
                {initialCheckedItems.length > 0 ? (
                  <>
                    {initialCheckedItems[0].label}
                    {initialCheckedItems.length > 1 && (
                      <>,{initialCheckedItems[1].label.slice(0, 3)}...</>
                    )}
                  </>
                ) : (
                  "Sub Asset Type "
                )}
              </span>
            </div>
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="min-w-[130px] xl:min-w-[160px] w-full">
        <div className="max-h-[251px] overflow-scroll w-full">
          {assetSubTypeOptions.map((item, index) => (
            <div key={index} className="flex items-center">
              <Checkbox
                id={`asset-${index}`}
                checkIconClassName="text-brand-bgBlack"
                className="data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack"
                checked={initialCheckedItems?.some(
                  (checkedItem: any) => checkedItem.value === item.value,
                )}
                onCheckedChange={(isChecked: boolean) =>
                  handleCheckboxChange(item, isChecked)
                }
              />
              <label
                htmlFor={`asset-${index}`}
                className="text-sm pl-2 py-1 font-openSans capitalize tracking-wider text-brand-textLightGrey"
              >
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
